/* eslint-disable @atlassian/relay/graphql-naming */
import React, { type PropsWithChildren, useCallback, useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, type RefetchFn, useFragment, useRelayEnvironment } from 'react-relay';
import { useFlagsService, toFlagId, type FlagConfiguration } from '@atlassian/jira-flags';
import { fg } from '@atlassian/jira-feature-gating';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ssr } from '@atlassian/jira-browser-metrics/src/bm3-init/ssr/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	useAnalyticsEvents,
	fireTrackAnalyticsDeferred,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueNavigatorIssueSearchRefetchQuery } from '../../__generated__/IssueNavigatorIssueSearchRefetchQuery.graphql';
import { PACKAGE_NAME, GROUPED_LIST_EXPERIENCE_ID } from '../../common/constants.tsx';
import type { IssueNavigatorViewId, OverridableIssueTableProps } from '../../common/types.tsx';
import { parseIssueNavigatorViewIdOrDefault } from '../../common/utils/index.tsx';
import { useActiveJql } from '../../services/active-jql/index.tsx';
import { useExperienceAnalytics } from '../../services/experience-analytics/index.tsx';
import { IssueSearchServiceContainer } from '../../services/issue-search/index.tsx';
import { useSelectedViewMutation } from '../../services/selected-view/useSelectedViewMutation.tsx';
import { useIssueNavigatorFeatures } from '../../controllers/features-context/index.tsx';
import type { issueSearch_issueNavigatorProject$key } from './__generated__/issueSearch_issueNavigatorProject.graphql';
import type { issueSearch_issueNavigatorGrouping$key } from './__generated__/issueSearch_issueNavigatorGrouping.graphql';
import messages from './messages.tsx';

const useNavigationItemId = createRouterSelector((state) => state.match?.params?.navigationItemId);

export type Props = PropsWithChildren<{
	/**
	 * Event emitted when page data has loaded and the key experience is interactive.
	 */
	onPageDataLoad?: (selectedView: IssueNavigatorViewId) => void;
	/**
	 * Fragment ref of the view currently rendered.
	 */
	jiraView: issueSearch_issueNavigatorGrouping$key | null;
	/**
	 * Relay refetch function to perform a new issue search.
	 */
	refetch: RefetchFn<IssueNavigatorIssueSearchRefetchQuery>;
	/**
	 * Flag indicating whether issue hierarchy feature is enabled in the experience
	 */
	isIssueHierarchySupportEnabled: boolean | undefined;
	/**
	 * Props for shadow requests.
	 */
	issueTableProps?: OverridableIssueTableProps;
	/**
	 * Fragment ref of the project.
	 */
	project: issueSearch_issueNavigatorProject$key | null;
	/**
	 * Flag indicating whether ifc feature is enabled in the experience
	 */
	isInlineFieldConfigEnabled?: boolean;
}>;

const searchErrorFlagId = toFlagId('ISSUE_NAVIGATOR_SEARCH_ERROR_FLAG');
const searchErrorFlag: FlagConfiguration = {
	id: searchErrorFlagId,
	type: 'error',
	title: messages.searchFailedTitle,
	description: messages.searchFailedDescription,
	messageId: `issue-navigator.ui.issue-search.searchErrorFlag.${searchErrorFlagId}`,
	messageType: 'transactional',
};

const isFieldPresentInJql = (query: string, fieldName: string) => {
	const regex = new RegExp(`\\b${fieldName}\\s*(=\\s*[^\\s]+|IN\\s*\\([^\\)]+\\))`, 'i');
	return regex.test(query);
};

export const IssueSearch = ({
	children,
	onPageDataLoad,
	refetch,
	isIssueHierarchySupportEnabled,
	isInlineFieldConfigEnabled,
	jiraView,
	issueTableProps,
	project,
}: Props) => {
	const { showFlag, dismissFlag } = useFlagsService();
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const { jql, filterId } = useActiveJql();
	const issueSearchInput = useMemo(
		() => (jql === undefined && isFilterId(filterId) ? { filterId } : { jql }),
		[filterId, jql],
	);

	if (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
	) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLabelsFieldPresentInJql = isFieldPresentInJql(issueSearchInput?.jql || '', 'labels');

		isLabelsFieldPresentInJql &&
			fireTrackAnalyticsDeferred(createAnalyticsEvent({}), 'labelsFilter used');
	}

	const setSelectedView = useSelectedViewMutation();

	const viewItemId = fg('saved_list_view_technical_changes')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useNavigationItemId()
		: undefined;

	const jiraViewData = useFragment<issueSearch_issueNavigatorGrouping$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueSearch_issueNavigatorGrouping on JiraView {
				... on JiraIssueSearchViewMetadata {
					viewId
				}
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						isGroupingEnabled
						canEnableGrouping
						groupByConfig {
							groupByField {
								fieldId
							}
						}
						isHierarchyEnabled
						hideDone
					}
				}
			}
		`,
		jiraView,
	);

	const projectData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueSearch_issueNavigatorProject on JiraProject
			@argumentDefinitions(
				isSavedListViewTechnicalChangesDogfoodingOrMergeSupportEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-dogfooding-or-merge-support-enabled.relayprovider"
				}
			) {
				projectId @include(if: $isSavedListViewTechnicalChangesDogfoodingOrMergeSupportEnabled)
			}
		`,
		project,
	);

	const groupedByField = jiraViewData?.viewSettings?.groupByConfig?.groupByField?.fieldId ?? null;
	const isGroupingEnabled =
		Boolean(issueTableProps?.isGroupingSupported) &&
		Boolean(jiraViewData?.viewSettings?.isGroupingEnabled) &&
		Boolean(jiraViewData?.viewSettings?.canEnableGrouping);
	const isHierarchyEnabled = Boolean(
		isIssueHierarchySupportEnabled && jiraViewData?.viewSettings?.isHierarchyEnabled,
	);
	const isHideDoneEnabled = Boolean(jiraViewData?.viewSettings?.hideDone);
	const maybeViewId = jiraViewData?.viewId;

	const onRefetchStart = useCallback(() => {
		// Hide any search error flags currently visible
		dismissFlag(searchErrorFlagId);
	}, [dismissFlag]);

	const viewId = parseIssueNavigatorViewIdOrDefault(maybeViewId);
	const { onIssueSearchFail } = useExperienceAnalytics(onPageDataLoad, {
		...(isGroupingEnabled ? { experienceId: GROUPED_LIST_EXPERIENCE_ID } : {}),
		isBifrost: !!ssr.getFeatureFlags()?.ssrViaBifrost,
	});
	const onRefetchError = useCallback(
		(error: Error, refetchView: IssueNavigatorViewId) => {
			onIssueSearchFail(`${PACKAGE_NAME}.issue-search-refetch`, error, refetchView);
			showFlag(searchErrorFlag);
		},
		[onIssueSearchFail, showFlag],
	);
	const projectContext = issueTableProps?.projectContext;

	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEnabled;

	const isSavedViewConditionalFormattingEnabled =
		isConditionalFormattingEnabled && fg('saved_list_view_technical_changes');

	return (
		<IssueSearchServiceContainer
			cloudId={cloudId}
			environment={environment}
			issueSearchInput={issueSearchInput}
			viewId={viewId}
			filterId={filterId}
			onPageDataLoad={onPageDataLoad}
			onRefetchStart={onRefetchStart}
			onRefetchError={onRefetchError}
			onSelectedViewMutation={setSelectedView}
			refetch={refetch}
			projectContext={projectContext}
			isHierarchyEnabled={isHierarchyEnabled}
			isHideDoneEnabled={isHideDoneEnabled}
			groupedByField={groupedByField}
			isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
			isConditionalFormattingEnabled={isConditionalFormattingEnabled}
			isSavedViewConditionalFormattingEnabled={isSavedViewConditionalFormattingEnabled}
			isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
			activationId={activationId}
			projectId={projectData?.projectId}
			viewItemId={viewItemId}
		>
			{children}
		</IssueSearchServiceContainer>
	);
};
